import React, { useState, useEffect, useMemo, FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter } from '../../router/Router';
import { validateEgyptianPhone } from '../../utils/errors';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import { universityService } from '../../services/universityService';
import { studentDemandService } from '../../services/studentDemandService';
import { University as UniversityType } from '../../types';
import {
  User,
  MapPin,
  GraduationCap,
  Phone,
  Mail,
  AlertCircle,
  Save,
  CheckCircle2,
  Building2,
  LogOut,
  Sparkles,
} from 'lucide-react';

const NEARBY_ALEX_GOVERNORATES = ['مطروح', 'البحيرة', 'المنوفية', 'الغربية', 'كفر الشيخ'];

const REGION_OPTIONS = [
  { id: 'NEARBY_ALEX', label: '⭐ الجامعات القريبة من الإسكندرية (العلمين ومطروح، البحيرة، مدينة السادات، طنطا، كفر الشيخ)' },
  { id: 'ALL', label: '🏛️ جميع الجامعات المسجلة بالمنظومة (عرض الكل)' },
  { id: 'مطروح', label: 'محافظة مطروح والساحل الشمالي والعلمين' },
  { id: 'البحيرة', label: 'محافظة البحيرة (دمنهور / رشيد)' },
  { id: 'المنوفية', label: 'محافظة المنوفية (مدينة السادات / شبين الكوم)' },
  { id: 'الغربية', label: 'محافظة الغربية (طنطا / سمنود)' },
  { id: 'كفر الشيخ', label: 'محافظة كفر الشيخ' },
  { id: 'الجيزة', label: 'محافظة الجيزة (6 أكتوبر / الشيخ زايد)' },
  { id: 'القاهرة', label: 'محافظة القاهرة (العاصمة والمدن الجديدة)' },
  { id: 'الدقهلية', label: 'محافظة الدقهلية (المنصورة / جمصة)' },
  { id: 'دمياط', label: 'محافظة دمياط' },
  { id: 'القليوبية', label: 'محافظة القليوبية (بنها / العبور)' },
  { id: 'الشرقية', label: 'محافظة الشرقية (الزقازيق)' },
  { id: 'OTHER', label: 'محافظة أخرى' },
];

export const CompleteProfile: React.FC = () => {
  const { user, studentProfile, completeProfile, isLoading, error, clearError, logout } = useAuth();
  const { navigate } = useRouter();

  const [allUniversities, setAllUniversities] = useState<UniversityType[]>(SEED_UNIVERSITIES);
  const [fullName, setFullName] = useState(studentProfile?.fullName || '');
  const [phoneNumber, setPhoneNumber] = useState(studentProfile?.phoneNumber || '');
  const [address, setAddress] = useState(studentProfile?.address || '');
  const [regionFilter, setRegionFilter] = useState<string>('NEARBY_ALEX');
  const [governorate, setGovernorate] = useState(
    studentProfile?.universityGovernorate && studentProfile?.universityGovernorate !== 'الإسكندرية'
      ? studentProfile.universityGovernorate
      : 'مطروح'
  );
  const [universityId, setUniversityId] = useState(studentProfile?.universityId || '');
  const [customUniversityName, setCustomUniversityName] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  // Load active universities from Firestore service & combine with SEED_UNIVERSITIES
  useEffect(() => {
    const fetchUnis = async () => {
      try {
        const list = await universityService.getActiveUniversities();
        const map = new Map<string, UniversityType>();
        SEED_UNIVERSITIES.forEach((u) => map.set(u.id, u));
        if (list && list.length > 0) {
          list.forEach((u) => map.set(u.id, u));
        }
        setAllUniversities(Array.from(map.values()));
      } catch {
        setAllUniversities(SEED_UNIVERSITIES);
      }
    };
    fetchUnis();
  }, []);

  // Sync if student profile loads asynchronously
  useEffect(() => {
    if (studentProfile) {
      if (studentProfile.fullName && !fullName) setFullName(studentProfile.fullName);
      if (studentProfile.phoneNumber && !phoneNumber) setPhoneNumber(studentProfile.phoneNumber);
      if (studentProfile.address && !address) setAddress(studentProfile.address);
      if (studentProfile.universityGovernorate) {
        setGovernorate(studentProfile.universityGovernorate);
        if (NEARBY_ALEX_GOVERNORATES.includes(studentProfile.universityGovernorate)) {
          setRegionFilter('NEARBY_ALEX');
        } else {
          setRegionFilter(studentProfile.universityGovernorate);
        }
      }
      if (studentProfile.universityId) {
        setUniversityId(studentProfile.universityId);
        if (studentProfile.universityId.startsWith('uni_unlisted') || studentProfile.universityId === 'other') {
          setCustomUniversityName(studentProfile.universityName || '');
          setUniversityId('other');
        }
      }
    }
  }, [studentProfile]);

  // Filter universities based on selected region / governorate
  const filteredUniversities = useMemo(() => {
    let list: UniversityType[] = [];
    if (regionFilter === 'NEARBY_ALEX') {
      list = allUniversities.filter((u) => NEARBY_ALEX_GOVERNORATES.includes(u.governorate));
    } else if (regionFilter === 'ALL' || regionFilter === 'OTHER') {
      list = allUniversities;
    } else {
      list = allUniversities.filter((u) => u.governorate === regionFilter);
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [allUniversities, regionFilter]);

  // Auto-sync governorate when a university is picked
  const handleUniversityChange = (val: string) => {
    setUniversityId(val);
    if (clientError) setClientError(null);

    if (val === 'other') {
      // User chose other - keep existing governorate or fallback
      if (!governorate || governorate === 'الإسكندرية') setGovernorate('مطروح');
    } else {
      const match = allUniversities.find((u) => u.id === val);
      if (match) {
        setGovernorate(match.governorate);
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setClientError(null);
    clearError();

    // 1. Full name validation
    if (!fullName.trim()) {
      setClientError('يرجى إدخال اسم الطالب بالكامل.');
      return;
    }

    if (fullName.trim().split(' ').length < 2) {
      setClientError('يرجى إدخال الاسم الثنائي على الأقل (مثال: أحمد محمد).');
      return;
    }

    // 2. Phone validation (Mandatory Egyptian mobile)
    const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
    if (!cleanPhone) {
      setClientError('رقم الهاتف المحمول (الواتساب) إجباري للتواصل عند وصول الحافلة للمحطة.');
      return;
    }

    if (!validateEgyptianPhone(cleanPhone)) {
      setClientError(
        'رقم الهاتف المحمول غير صحيح (يجب أن يكون رقم مصري مكون من 11 رقماً يبدأ بـ 010 أو 011 أو 012 أو 015).'
      );
      return;
    }

    // 3. Address in Alexandria
    if (!address.trim()) {
      setClientError('يرجى إدخال منطقة أو عنوان السكن بالإسكندرية (لتحديد أقرب نقطة تجمع للركوب).');
      return;
    }

    // 4. University selection validation
    if (!universityId) {
      setClientError('يرجى اختيار الجامعة المقيد بها من القائمة أو اختيار "أخرى" لكتابة اسمها يدوياً.');
      return;
    }

    if (universityId === 'other' && !customUniversityName.trim()) {
      setClientError('يرجى كتابة اسم الجامعة أو الأكاديمية يدوياً في الخانة المخصصة.');
      return;
    }

    const selectedUni = allUniversities.find((u) => u.id === universityId);
    const resolvedUniversityName =
      universityId === 'other'
        ? customUniversityName.trim()
        : selectedUni?.name || 'جامعة غير محددة';

    const finalGov =
      universityId === 'other'
        ? (governorate.trim() || 'مطروح')
        : (selectedUni?.governorate || governorate.trim() || 'مطروح');

    const isWest =
      address.includes('21') ||
      address.includes('عجمي') ||
      address.includes('عامرية') ||
      address.includes('العجمي') ||
      address.includes('العامرية') ||
      address.includes('الدخيلة') ||
      address.includes('دخيلة');
    const defaultStation = isWest ? 'الكيلو 21' : 'الموقف الجديد بمحرم بيك';
    const assignedRoute = `${defaultStation} ◄ ${resolvedUniversityName}`;

    const success = await completeProfile({
      fullName: fullName.trim(),
      phoneNumber: cleanPhone,
      address: address.trim(),
      universityGovernorate: finalGov,
      universityId: universityId === 'other' ? `uni_unlisted_${Date.now()}` : universityId,
      universityName: resolvedUniversityName,
      defaultPickupStation: defaultStation,
      assignedRoute: assignedRoute,
    });

    if (success) {
      try {
        const finalUniId = universityId === 'other' ? `uni_unlisted_${Date.now()}` : universityId;
        await studentDemandService.recordStudentProfileDemand({
          studentId: user?.uid || undefined,
          studentName: fullName.trim(),
          phone: cleanPhone,
          universityId: finalUniId,
          universityName: resolvedUniversityName,
          universityGovernorate: finalGov,
          address: address.trim(),
          assignedRoute: assignedRoute,
          pickupStation: defaultStation,
        });
      } catch (dErr) {
        console.warn('Auto recording student university demand failed:', dErr);
      }
      navigate('/student');
    }
  };

  const activeError = clientError || error?.messageAr;

  return (
    <div className="auth-page-wrapper" dir="rtl">
      <div className="auth-card profile-completion-card">
        {/* Top Header */}
        <div className="auth-card-header">
          <div
            className="auth-brand-badge"
            style={{
              padding: '0',
              overflow: 'hidden',
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              margin: '0 auto 1rem',
            }}
          >
            <img
              src="/oto-logo.jpg"
              alt="OTO"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <h1 className="auth-title">استكمال بيانات الطالب</h1>
          <p className="auth-subtitle">
            خطوة واحدة تفصلك عن حجز رحلاتك الجامعية مع <strong>OTO STUDENTS</strong>
          </p>
        </div>

        {/* Step Indicator */}
        <div className="profile-steps-indicator">
          <div className="step-badge active">
            <CheckCircle2 size={16} />
            <span>1. الحساب موثق</span>
          </div>
          <div className="step-arrow">◄</div>
          <div className="step-badge current">
            <span>2. بيانات الطالب والجامعة</span>
          </div>
          <div className="step-arrow">◄</div>
          <div className="step-badge">
            <span>3. حجز المقعد</span>
          </div>
        </div>

        {/* Error Banner */}
        {activeError && (
          <div className="auth-error-banner" role="alert">
            <AlertCircle className="auth-error-icon" size={20} />
            <span>{activeError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {/* Email (Read Only Identity) */}
          <div className="form-group">
            <label className="form-label label-with-meta">
              <span>البريد الإلكتروني المعتمد</span>
              <span className="badge-verified">موثق في Firebase Auth</span>
            </label>
            <div className="input-with-icon input-readonly">
              <Mail className="input-icon" size={18} />
              <input
                type="email"
                className="form-input readonly-input"
                value={user?.email || studentProfile?.email || ''}
                readOnly
                disabled
              />
            </div>
            <p className="form-helper-text">
              هذا هو المعرف الرسمي لحسابك ولا يمكن تغييره لضمان أمان الحساب.
            </p>
          </div>

          {/* Full Name */}
          <div className="form-group">
            <label htmlFor="profile-fullName" className="form-label">
              الاسم بالكامل <span className="required-star">*</span>
            </label>
            <div className="input-with-icon">
              <User className="input-icon" size={18} />
              <input
                id="profile-fullName"
                type="text"
                className="form-input"
                placeholder="أحمد محمد علي"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* Phone Number (Mandatory) */}
          <div className="form-group">
            <label htmlFor="profile-phone" className="form-label label-with-meta">
              <span>رقم الهاتف المحمول (الواتساب) للتواصل بالمحطة</span>
              <span style={{ color: '#E5232A', fontSize: '11px', fontWeight: 'bold' }}>* إجباري</span>
            </label>
            <div className="input-with-icon">
              <Phone className="input-icon" size={18} />
              <input
                id="profile-phone"
                type="tel"
                className="form-input"
                dir="ltr"
                placeholder="01012345678"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                required
              />
            </div>
            <p className="form-helper-text">
              يُستخدم للتواصل الهاتفي والواتساب عند وصول الحافلة لنقطة التجمع بالإسكندرية (11 رقماً).
            </p>
          </div>

          {/* Address in Alexandria */}
          <div className="form-group">
            <label htmlFor="profile-address" className="form-label">
              منطقة أو عنوان السكن بالإسكندرية <span className="required-star">*</span>
            </label>
            <div className="input-with-icon">
              <MapPin className="input-icon" size={18} />
              <input
                id="profile-address"
                type="text"
                className="form-input"
                placeholder="مثال: سموحة - ش فوزي معاذ، أو سيدي بشر، العصافرة..."
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                required
              />
            </div>
            <p className="form-helper-text">
              لتحديد أقرب محطة أو نقطة تجمع مناسبة لك في خطوط سير الحافلات.
            </p>
          </div>

          {/* Region / Governorate Filter */}
          <div className="form-group">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="profile-region" className="form-label" style={{ marginBottom: 0 }}>
                تصفية الجامعات حسب المنطقة أو المحافظة <span className="required-star">*</span>
              </label>
              <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200 flex items-center gap-1">
                <Sparkles size={11} />
                <span>الجامعات المستهدفة خارج الإسكندرية</span>
              </span>
            </div>
            <div className="input-with-icon">
              <Building2 className="input-icon" size={18} />
              <select
                id="profile-region"
                className="form-select font-semibold text-slate-800"
                value={regionFilter}
                onChange={(e) => {
                  setRegionFilter(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
              >
                {REGION_OPTIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* University Selection Dropdown */}
          <div className="form-group">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="profile-uni" className="form-label" style={{ marginBottom: 0 }}>
                الجامعة المقيد بها الطالب <span className="required-star">*</span>
              </label>
              {universityId === 'other' && (
                <span className="text-[11px] font-bold text-[#E5232A] bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                  إدخال اسم الجامعة يدوياً
                </span>
              )}
            </div>

            <div className="input-with-icon">
              <GraduationCap className="input-icon" size={18} />
              <select
                id="profile-uni"
                className="form-select"
                value={universityId}
                onChange={(e) => handleUniversityChange(e.target.value)}
                disabled={isLoading}
              >
                <option value="">-- اختر الجامعة من القائمة المسجلة --</option>
                {filteredUniversities.map((uni) => (
                  <option key={uni.id} value={uni.id}>
                    {uni.name} ({uni.campus || uni.city || uni.governorate})
                  </option>
                ))}
                <option
                  value="other"
                  style={{ fontWeight: 'bold', color: '#E5232A', backgroundColor: '#FFF1F2' }}
                >
                  ✏️ أخرى / غير مدرجة بالقائمة (إدخال اسم الجامعة يدوياً...)
                </option>
              </select>
            </div>

            {/* Manual University Input Card (Shows when 'other' is selected) */}
            {universityId === 'other' && (
              <div
                className="mt-3 p-3.5 rounded-xl border border-rose-200 space-y-3"
                style={{ backgroundColor: '#FFF5F5' }}
              >
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    اسم الجامعة أو الأكاديمية (إدخال يدوي) <span className="text-[#E5232A]">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input bg-white text-sm"
                    style={{ borderColor: '#F87171' }}
                    placeholder="مثال: جامعة الجلالة الدولية، جامعة بدر، أكاديمية السادات..."
                    value={customUniversityName}
                    onChange={(e) => {
                      setCustomUniversityName(e.target.value);
                      if (clientError) setClientError(null);
                    }}
                    disabled={isLoading}
                    autoFocus
                    required
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    سيتم تسجيل رغبتك بالمنظومة لدراسة توفير خط نقل مباشر لك ولزملائك.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    المحافظة أو المدينة التابعة لها هذه الجامعة (اختياري)
                  </label>
                  <input
                    type="text"
                    className="form-input bg-white text-sm"
                    placeholder="مثال: السويس، الإسماعيلية، الشرقية، بورسعيد..."
                    value={governorate}
                    onChange={(e) => setGovernorate(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-primary auth-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="btn-loading-content">
                <span className="spinner-inline"></span>
                جاري حفظ البيانات...
              </span>
            ) : (
              <span className="btn-normal-content">
                <Save size={18} />
                حفظ ومتابعة
              </span>
            )}
          </button>
        </form>

        {/* Logout option if student logged in with wrong account */}
        <div className="auth-card-footer">
          <button
            type="button"
            className="text-btn logout-link-btn"
            onClick={logout}
            disabled={isLoading}
          >
            <LogOut size={15} />
            <span>تسجيل الخروج من هذا الحساب</span>
          </button>
        </div>
      </div>
    </div>
  );
};
